import { useState, useEffect } from 'react';
import {
  User, ShoppingBag, MapPin, Phone, Mail,
  ChevronDown, ChevronUp, Star,
  AlertCircle, UserPlus, Truck,
  CreditCard, Percent, StickyNote, Copy, Check
} from 'lucide-react';
import { getShopifyCustomerProfile, ShopifyCustomerProfile } from '../../services/api';

interface ShopifyCustomerCardProps {
  email: string;
  customerName?: string;
}

// Module-level cache: email → { data, timestamp }
const profileCache = new Map<string, { data: ShopifyCustomerProfile; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached(email: string): ShopifyCustomerProfile | null {
  const entry = profileCache.get(email);
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  return null;
}

function setCache(email: string, data: ShopifyCustomerProfile) {
  profileCache.set(email, { data, ts: Date.now() });
}

const FULFILLMENT_LABELS: Record<string, { label: string; color: string }> = {
  fulfilled: { label: 'Fulfilled', color: 'text-emerald-700 bg-emerald-50 ring-1 ring-inset ring-emerald-600/20' },
  partial: { label: 'Partial', color: 'text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20' },
  unfulfilled: { label: 'Unfulfilled', color: 'text-gray-600 bg-gray-100 ring-1 ring-inset ring-gray-500/20' },
  restocked: { label: 'Restocked', color: 'text-blue-700 bg-blue-50 ring-1 ring-inset ring-blue-600/20' },
};

const FINANCIAL_LABELS: Record<string, { label: string; color: string }> = {
  paid: { label: 'Paid', color: 'text-emerald-700 bg-emerald-50 ring-1 ring-inset ring-emerald-600/20' },
  pending: { label: 'Pending', color: 'text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20' },
  refunded: { label: 'Refunded', color: 'text-red-700 bg-red-50 ring-1 ring-inset ring-red-600/20' },
  partially_refunded: { label: 'Partial Refund', color: 'text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-600/20' },
  voided: { label: 'Voided', color: 'text-gray-600 bg-gray-100 ring-1 ring-inset ring-gray-500/20' },
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: string, currency?: string) {
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="opacity-0 group-hover/copy:opacity-100 text-gray-400 hover:text-gray-600 transition-all ml-auto flex-shrink-0"
      title="Copy"
    >
      {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
    </button>
  );
}

export function ShopifyCustomerCard({ email, customerName }: ShopifyCustomerCardProps) {
  const cached = getCached(email);
  const [profile, setProfile] = useState<ShopifyCustomerProfile | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [ordersExpanded, setOrdersExpanded] = useState(true);
  const [contactExpanded, setContactExpanded] = useState(false);

  useEffect(() => {
    if (!email) return;

    const cachedData = getCached(email);
    if (cachedData) {
      setProfile(cachedData);
      setLoading(false);
      return;
    }

    setLoading(true);
    setProfile(null);

    getShopifyCustomerProfile(email)
      .then((data) => {
        setCache(email, data);
        setProfile(data);
      })
      .catch(() => {
        setProfile({ found: false, error: 'Failed to load profile' });
      })
      .finally(() => setLoading(false));
  }, [email]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="flex-shrink-0 bg-white border-b border-gray-200/80">
        <div className="px-5 py-4">
          <div className="animate-pulse space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-gray-200 rounded w-36" />
                <div className="h-3 bg-gray-200 rounded w-48" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-1">
              <div className="h-12 bg-gray-100 rounded-lg" />
              <div className="h-12 bg-gray-100 rounded-lg" />
              <div className="h-12 bg-gray-100 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / Shopify not connected
  if (profile?.error) {
    return (
      <div className="flex-shrink-0 bg-white border-b border-gray-200/80 px-5 py-3">
        <div className="flex items-center gap-2.5 text-[12px]">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <User size={14} className="text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-900">{customerName || 'Customer'}</span>
            <p className="text-[11px] text-gray-500 truncate">{email}</p>
          </div>
          <span className="text-[10px] text-gray-400 flex items-center gap-1">
            <AlertCircle size={10} />
            {profile.error.includes('not connected')
              ? 'Connect Shopify'
              : 'Profile unavailable'}
          </span>
        </div>
      </div>
    );
  }

  // New customer / not found in Shopify
  if (profile && !profile.found) {
    return (
      <div className="flex-shrink-0 bg-white border-b border-gray-200/80 px-5 py-3">
        <div className="flex items-center gap-2.5 text-[12px]">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
            <UserPlus size={14} className="text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-900">{customerName || 'Customer'}</span>
            <p className="text-[11px] text-gray-500 truncate">{email}</p>
          </div>
          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-600 rounded-full">
            New customer
          </span>
        </div>
      </div>
    );
  }

  // No profile loaded yet (shouldn't happen, but fallback)
  if (!profile?.customer) {
    return (
      <div className="flex-shrink-0 bg-white border-b border-gray-200/80 px-5 py-3">
        <div className="flex items-center gap-2.5 text-[12px]">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <User size={14} className="text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-900">{customerName || 'Customer'}</span>
            <p className="text-[11px] text-gray-500 truncate">{email}</p>
          </div>
        </div>
      </div>
    );
  }

  const { customer, recent_orders } = profile;
  const currency = customer.currency || 'USD';

  return (
    <div className="flex-shrink-0 border-b border-gray-200/80 bg-white">
      {/* ── Customer Header ── */}
      <div className="px-5 pt-4 pb-3">
        {/* Identity row */}
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <span className="text-sm font-semibold text-white">
              {customer.name?.[0]?.toUpperCase() || 'C'}
            </span>
          </div>

          {/* Name + email */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold text-gray-900 truncate">{customer.name}</span>
              {customer.is_vip && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full ring-1 ring-inset ring-amber-200">
                  <Star size={8} className="fill-amber-500 text-amber-500" />
                  VIP
                </span>
              )}
              {customer.accepts_marketing && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium bg-emerald-50 text-emerald-600 rounded-full ring-1 ring-inset ring-emerald-200">
                  Subscribed
                </span>
              )}
            </div>
            <p className="text-[12px] text-gray-500 truncate mt-0.5">{customer.email}</p>
          </div>
        </div>

        {/* Stats row — 3 evenly spaced cards */}
        <div className="grid grid-cols-3 gap-2.5 mt-3">
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Orders</p>
            <p className="text-[16px] font-bold text-gray-900 mt-0.5">{customer.orders_count}</p>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Lifetime</p>
            <p className="text-[14px] font-bold text-gray-900 mt-0.5">{formatCurrency(customer.total_spent, currency)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Since</p>
            <p className="text-[13px] font-semibold text-gray-700 mt-0.5">
              {new Date(customer.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Tags */}
        {customer.tags.length > 0 && (
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {customer.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Customer note */}
        {customer.note && (
          <div className="flex items-start gap-2 mt-3 px-3 py-2 bg-amber-50 rounded-lg ring-1 ring-inset ring-amber-100">
            <StickyNote size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <span className="text-[11px] text-amber-800 leading-relaxed">{customer.note}</span>
          </div>
        )}
      </div>

      {/* ── Contact & Address (collapsible) ── */}
      {(customer.default_address?.address1 || customer.phone) && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setContactExpanded(!contactExpanded)}
            className="w-full flex items-center justify-between px-5 py-2 text-[12px] text-gray-600 hover:bg-gray-50/80 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium">
              <User size={13} className="text-gray-400" />
              Contact & Address
            </span>
            {contactExpanded ? <ChevronUp size={13} className="text-gray-400" /> : <ChevronDown size={13} className="text-gray-400" />}
          </button>

          {contactExpanded && (
            <div className="px-5 pb-3 space-y-1">
              {/* Phone */}
              {customer.phone && (
                <div className="group/copy flex items-center gap-2.5 text-[12px] py-1 px-2 rounded-md hover:bg-gray-50 transition-colors">
                  <Phone size={12} className="text-gray-400 flex-shrink-0" />
                  <span className="text-gray-700">{customer.phone}</span>
                  <CopyButton text={customer.phone} />
                </div>
              )}
              {/* Address phone (shipping, if different) */}
              {customer.default_address?.phone && customer.default_address.phone !== customer.phone && (
                <div className="group/copy flex items-center gap-2.5 text-[12px] py-1 px-2 rounded-md hover:bg-gray-50 transition-colors">
                  <Phone size={12} className="text-gray-400 flex-shrink-0" />
                  <span className="text-gray-700">{customer.default_address.phone}</span>
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">shipping</span>
                  <CopyButton text={customer.default_address.phone} />
                </div>
              )}
              {/* Email */}
              <div className="group/copy flex items-center gap-2.5 text-[12px] py-1 px-2 rounded-md hover:bg-gray-50 transition-colors">
                <Mail size={12} className="text-gray-400 flex-shrink-0" />
                <span className="text-gray-700">{customer.email}</span>
                <CopyButton text={customer.email} />
              </div>
              {/* Full address */}
              {customer.default_address?.address1 && (
                <div className="flex items-start gap-2.5 text-[12px] py-1 px-2 rounded-md">
                  <MapPin size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="text-gray-700 leading-relaxed">
                    <div>{customer.default_address.address1}</div>
                    {customer.default_address.address2 && <div>{customer.default_address.address2}</div>}
                    <div>
                      {[
                        customer.default_address.zip,
                        customer.default_address.city,
                        customer.default_address.province,
                      ].filter(Boolean).join(', ')}
                    </div>
                    {customer.default_address.country && (
                      <div className="text-gray-500">{customer.default_address.country}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Recent Orders (collapsible) ── */}
      {recent_orders && recent_orders.length > 0 && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setOrdersExpanded(!ordersExpanded)}
            className="w-full flex items-center justify-between px-5 py-2 text-[12px] text-gray-600 hover:bg-gray-50/80 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium">
              <ShoppingBag size={13} className="text-gray-400" />
              Recent Orders
              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full font-normal">{recent_orders.length}</span>
            </span>
            {ordersExpanded ? <ChevronUp size={13} className="text-gray-400" /> : <ChevronDown size={13} className="text-gray-400" />}
          </button>

          {ordersExpanded && (
            <div className="px-5 pb-3 space-y-2">
              {recent_orders.map((order) => {
                const fulfillment = FULFILLMENT_LABELS[order.fulfillment_status] || {
                  label: order.fulfillment_status || 'Unknown',
                  color: 'text-gray-600 bg-gray-100 ring-1 ring-inset ring-gray-500/20',
                };
                const financial = FINANCIAL_LABELS[order.financial_status] || {
                  label: order.financial_status || 'Unknown',
                  color: 'text-gray-600 bg-gray-100 ring-1 ring-inset ring-gray-500/20',
                };

                return (
                  <div
                    key={order.order_number}
                    className="rounded-lg border border-gray-100 bg-white hover:border-gray-200 transition-colors overflow-hidden"
                  >
                    {/* Order header */}
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50/50">
                      <div className="flex items-center gap-2">
                        {order.admin_url ? (
                          <a
                            href={order.admin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[13px] font-semibold text-violet-600 hover:text-violet-700 hover:underline"
                          >
                            {order.order_number}
                          </a>
                        ) : (
                          <span className="text-[13px] font-semibold text-gray-900">{order.order_number}</span>
                        )}
                        <span className="text-[11px] text-gray-400">{formatDate(order.created_at)}</span>
                      </div>
                      <span className="text-[13px] font-semibold text-gray-900">{order.total}</span>
                    </div>

                    {/* Status badges */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full ${fulfillment.color}`}>
                        {fulfillment.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full ${financial.color}`}>
                        {financial.label}
                      </span>
                    </div>

                    {/* Line items */}
                    {order.items_detail && order.items_detail.length > 0 && (
                      <div className="px-3 pb-2 space-y-1">
                        {order.items_detail.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-[11px] py-0.5">
                            <span className="text-gray-400 w-5 text-right flex-shrink-0 font-medium">{item.quantity}x</span>
                            <span className="text-gray-700 truncate flex-1">{item.title}{item.variant_title ? ` — ${item.variant_title}` : ''}</span>
                            <span className="text-gray-500 flex-shrink-0 tabular-nums">{item.price} {currency}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Shipping / Payment / Discount footer */}
                    {(order.shipping_method || order.payment_method || order.discount) && (
                      <div className="flex items-center gap-3 px-3 py-1.5 border-t border-gray-50 flex-wrap">
                        {order.shipping_method && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                            <Truck size={10} />
                            {order.shipping_method}
                          </span>
                        )}
                        {order.payment_method && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                            <CreditCard size={10} />
                            {order.payment_method}
                          </span>
                        )}
                        {order.discount && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                            <Percent size={10} />
                            -{order.discount.amount} ({order.discount.code})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
